import { useState } from 'react';
import { useAppStore } from '@/lib/store';
import { formatTrx } from '@/lib/utils';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Brush,
} from 'recharts';

export default function ProjectionChart() {
  const { rows } = useAppStore();
  const [brushRange, setBrushRange] = useState<[number, number]>([
    0,
    Math.min(30, rows.length - 1),
  ]);

  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        No data available. Please configure the settings to see projections.
      </div>
    );
  }

  // Filter data based on brush range
  const filteredData = rows.slice(brushRange[0], brushRange[1] + 1);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="card p-3 shadow-lg border-0">
          <p className="font-medium">Day {label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const formatYAxis = (tickItem: number) => {
    if (tickItem >= 1000) {
      return `${(tickItem / 1000).toFixed(1)}K`;
    }
    return tickItem.toString();
  };

  return (
    <div className="w-full h-96">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={filteredData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis dataKey="day" stroke="#9CA3AF" tick={{ fontSize: 12 }} />
          <YAxis
            yAxisId="left"
            stroke="#9CA3AF"
            tick={{ fontSize: 12 }}
            tickFormatter={formatYAxis}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            stroke="#9CA3AF"
            tick={{ fontSize: 12 }}
            tickFormatter={(value) => formatTrx(value)}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />

          {/* Energy per day */}
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="energyDay"
            stroke="#3B82F6"
            strokeWidth={2}
            dot={false}
            name="Energy/Day"
          />

          {/* TRX needed */}
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="totalTrxNeeded"
            stroke="#10B981"
            strokeWidth={2}
            dot={false}
            name="TRX Required"
          />

          {/* Burned TRX */}
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="burnedTrxIfShort"
            stroke="#EF4444"
            strokeWidth={2}
            dot={false}
            name="TRX Burned (if under-staked)"
          />

          {/* Transfers per day */}
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="tpd"
            stroke="#8B5CF6"
            strokeWidth={2}
            dot={false}
            name="Transfers/Day"
          />

          <Brush
            dataKey="day"
            height={30}
            stroke="#8884d8"
            onChange={(brushData) => {
              if (
                brushData &&
                brushData.startIndex !== undefined &&
                brushData.endIndex !== undefined
              ) {
                setBrushRange([brushData.startIndex, brushData.endIndex]);
              }
            }}
          />
        </LineChart>
      </ResponsiveContainer>

      <div className="mt-4 text-sm text-muted-foreground text-center">
        Use the brush below the chart to zoom into specific time ranges
      </div>
    </div>
  );
}
